import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import { LandingPage } from "@/pages/LandingPage";
import { LoginPage } from "@/pages/LoginPage";
import { AccessByInvitationPage } from "@/pages/AccessByInvitationPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { GuiaVisualPage } from "@/pages/GuiaVisualPage";
import { FlujosPage } from "@/pages/FlujosPage";
import { UsersPage } from "@/pages/admin/UsersPage";
import { UnitsPage } from "@/pages/admin/UnitsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { MeetingsPage } from "@/pages/MeetingsPage";
import { MeetingDetailPage } from "@/pages/MeetingDetailPage";
import { ResolutionsPage } from "@/pages/ResolutionsPage";
import { FinanceDashboardPage } from "@/pages/finance/FinanceDashboardPage";
import { FeesPage } from "@/pages/finance/FeesPage";
import { FeeRemindersPage } from "@/pages/finance/FeeRemindersPage";
import { RolesPage } from "@/pages/admin/RolesPage";
import { ExpensesPage } from "@/pages/finance/ExpensesPage";
import { ReportsPage } from "@/pages/finance/ReportsPage";
import { BudgetsPage } from "@/pages/finance/BudgetsPage";
import { MonthlyClosePage } from "@/pages/finance/MonthlyClosePage";
import { ResidentFinancesPage } from "@/pages/resident/ResidentFinancesPage";
import { ResidentDashboardPage } from "@/pages/resident/ResidentDashboardPage";
import { WorksPermitsPage } from "@/pages/resident/WorksPermitsPage";
import { MaintenancePage } from "@/pages/resident/MaintenancePage";
import { AnnouncementsPage } from "@/pages/resident/AnnouncementsPage";
import { ResidentFeesPage } from "@/pages/resident/ResidentFeesPage";
import { IncidentsPage } from "@/pages/security/IncidentsPage";
import { KeysPage } from "@/pages/security/KeysPage";
import { CamerasPage } from "@/pages/security/CamerasPage";
import { StaffPage } from "@/pages/admin/StaffPage";
import { StaffDetailPage } from "@/pages/admin/StaffDetailPage";
import { DocumentsPage } from "@/pages/legal/DocumentsPage";
import { ContractsPage } from "@/pages/legal/ContractsPage";
import { LegalActionsPage } from "@/pages/legal/LegalActionsPage";
import { AuditLogPage } from "@/pages/admin/AuditLogPage";
import { OperationsQueuePage } from "@/pages/OperationsQueuePage";
import { HelpHomePage } from "@/pages/help/HelpHomePage";
import { HelpTourPage } from "@/pages/help/HelpTourPage";
import { HelpScreensPage } from "@/pages/help/HelpScreensPage";
import { HelpOperationsPage } from "@/pages/help/HelpOperationsPage";
import { HelpLayout } from "@/components/help/HelpLayout";
import { WizardProvider } from "@/components/wizard/WizardProvider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// Residents get their own dashboard; admin/board get the management dashboard.
// (The admin dashboard summary API no longer returns financial KPIs to residents.)
function DashboardRouter() {
  const { user } = useAuth();
  return user?.role === "RESIDENT" ? <ResidentDashboardPage /> : <DashboardPage />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Self-registration is retired; the path is kept so old links explain themselves
                instead of being swallowed by the catch-all redirect below. */}
            <Route path="/register" element={<AccessByInvitationPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Public visual guide / tour — accessible without login */}
            <Route path="/guia" element={<GuiaVisualPage />} />
            <Route path="/tour" element={<Navigate to="/guia" replace />} />
            {/* Public flow reference (one role's task per slide) — accessible without login */}
            <Route path="/flujos" element={<FlujosPage />} />
            {/* Public help routes — accessible without login */}
            <Route path="/help" element={<HelpLayout><HelpHomePage /></HelpLayout>} />
            <Route path="/help/tour" element={<HelpLayout><HelpTourPage /></HelpLayout>} />
            <Route path="/help/operations" element={<HelpLayout><HelpOperationsPage /></HelpLayout>} />
            <Route path="/help/screens" element={<HelpLayout><HelpScreensPage /></HelpLayout>} />
            <Route
              element={
                <ProtectedRoute>
                  <WizardProvider>
                    <AppLayout />
                  </WizardProvider>
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardRouter />} />
              <Route
                path="/meetings"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]}>
                    <MeetingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meetings/:id"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]}>
                    <MeetingDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resolutions"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]}>
                    <ResolutionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/operations-queue"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]}>
                    <OperationsQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/dashboard"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]} capability="finance:manage">
                    <FinanceDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/fees"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]} capability="finance:manage">
                    <FeesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reminders"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]} capability="finance:manage">
                    <FeeRemindersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/expenses"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]} capability="finance:manage">
                    <ExpensesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]} capability="finance:manage">
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/budgets"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]} capability="finance:manage">
                    <BudgetsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/close"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]} capability="finance:manage">
                    <MonthlyClosePage />
                  </ProtectedRoute>
                }
              />
              <Route path="/resident/finances" element={<ResidentFinancesPage />} />
              <Route path="/resident/dashboard" element={<ResidentDashboardPage />} />
              <Route path="/resident/works-permit" element={<WorksPermitsPage />} />
              <Route path="/resident/maintenance" element={<MaintenancePage />} />
              <Route path="/resident/announcements" element={<AnnouncementsPage />} />
              <Route path="/resident/fees" element={<ResidentFeesPage />} />
              <Route
                path="/admin/incidents"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]}>
                    <IncidentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/keys"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <KeysPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/cameras"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <CamerasPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/staff"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <StaffPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/staff/:id"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <StaffDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <DocumentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contracts"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]}>
                    <ContractsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/legal-actions"
                element={
                  <ProtectedRoute allowed={["ADMIN", "BOARD_MEMBER"]}>
                    <LegalActionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <RolesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/units"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <UnitsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit-log"
                element={
                  <ProtectedRoute allowed={["ADMIN"]}>
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />
              {/* Module routes will be added here */}
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
